import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../Layout/Layout';
import LazyImage from '../SharedComponents/ShowMedia/LazyImage';
import PreviewMediaDialog from '../SharedComponents/ShowMedia/PreviewMediaDialog';
import EmptyState from '../SharedComponents/Forms/EmptyState';
import QuickShowWizard from '../SharedComponents/QuickShow/QuickShowWizard';

import UploadPopup from './components/UploadPopup/UploadPopup';
import CreateFolderPopup from './components/Folder/CreateFolder';
import BreadCrumb from './components/MediaLayout/BreadCrumb';
import UpdateMedia from './components/MediaLayout/UpdateMedia';
import MoveMediaFolder from './components/FolderDragDrop/MoveMediaFolder';
import DeleteMediaDialog from './components/MediaLayout/DeleteMediaDialog';
import UploadToPlaylist from './components/MediaLayout/UploadToPlaylist';
import FirebaseMediaListener from '../Services/FirebaseMediaListener';

import folderIcon from '../../assets/img/folder.png';

import { useGalleryContext } from '../Contexts/GalleryContext';
import { useTitle, formatSize, formatDuration } from '../../config/global';
import { MEDIA_DEFAULT_FILTERS, MEDIA_TYPE_OPTIONS, SORT_OPTIONS, EMPTY_FOLDER } from '../../config/media';
import {
  useGetFolderName,
  useDeleteMedia,
  useGetMediaUsage,
  useDeleteMultiFolderMedia,
} from '../../queries';
import {
  setData, setParams, setRerender, galleryRefetch, deleteItemLocally,
} from '../../redux/Slices/scrollSlice';
import { toggleDialog, toggleDeleteDialog } from '../../redux/Slices/Global/globalSlice';
import { useFetchDataByPathMutation } from '../../queries/dataTable';
import { useCustomToast } from '../Contexts/ToastContext';

import './css/mediaGallery.css';
import { formatDateTime } from '../../utils/datetime';

/**
 * Media Gallery — rebuilt from scratch.
 *
 * Visual layer only — reuses every existing react-query hook, Redux slice,
 * and dialog component. Replaces the patched-together index.jsx + the
 * MediaCard/FolderCard/FolderDragDrop trio with a single coherent file.
 *
 * Layout:
 *   - Top bar: BreadCrumb + Search input + Upload button (right)
 *   - Toolbar: New Folder, Sort, Filter, Select, View toggle (grid/list)
 *   - Bulk-select bar: replaces toolbar when items are selected
 *   - Grid view OR List view (toggled via GalleryContext.layout)
 *   - Empty state when 0 items
 *
 * Existing dialogs reused (mounted at the bottom of the page):
 *   UploadPopup, CreateFolderPopup, UpdateMedia, UploadToPlaylist,
 *   MoveMediaFolder, DeleteMediaDialog, QuickShowWizard, PrimeReact's
 *   confirmDialog for the multi-delete confirm.
 */

export default function MediaGallery() {
  useTitle('Media');
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: routeFolderId } = useParams();

  const {
    multiSelectCheck, setMultiSelectCheck,
    selectedItem, setSelectedItem,
    idsList, setIdsList,
    layout, setLayout,
  } = useGalleryContext();

  const { isFetching, data, params, rerender } = useSelector((s) => s.scroll);
  const { openDialog, deleteDialog } = useSelector((s) => s.global);
  const fetchMediaUsage = useGetMediaUsage();
  const deleteMedia = useDeleteMedia();
  const deleteMulti = useDeleteMultiFolderMedia();
  const fetcher = useFetchDataByPathMutation();

  const [filters, setFilters] = useState(MEDIA_DEFAULT_FILTERS);
  const [currentFolder, setCurrentFolder] = useState(EMPTY_FOLDER);
  const [breadCrumb, setBreadCrumb] = useState(JSON.parse(localStorage.getItem('bread_crumb') ?? 'null') ?? []);
  const [quickShowItem, setQuickShowItem] = useState(null);

  const { data: fetchedFolder } = useGetFolderName(routeFolderId);

  // ── Sync route → folder (so deep-linking into /media/gallery/:id works) ──
  useEffect(() => {
    if (!routeFolderId) {
      setCurrentFolder(EMPTY_FOLDER);
      setBreadCrumb([]);
      localStorage.setItem('bread_crumb', JSON.stringify([]));
      localStorage.setItem('folder_id', '');
      dispatch(setParams({ ...filters, folder_id: null }));
      return;
    }
    if (fetchedFolder) {
      const bc = fetchedFolder.breadcrumb ?? [];
      const last = bc[bc.length - 1];
      if (last) {
        setCurrentFolder({ id: last.id, name: last.label });
        setBreadCrumb(bc);
        localStorage.setItem('bread_crumb', JSON.stringify(bc));
        localStorage.setItem('folder_id', last.id);
        dispatch(setParams({ ...filters, folder_id: last.id }));
      }
    }
  }, [routeFolderId, fetchedFolder]);

  // ── Fetcher (page 1 only; old infinite-scroll kept simple here) ───────────
  const refetch = () =>
    fetcher.mutateAsync(
      { path: '/folder/media', pageNum: 1, params: { ...params, section: 'MEDIA' } },
      { onSuccess: (res) => dispatch(setData(res?.data ?? [])) }
    );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  /* `galleryRefetch()` (dispatched by UploadMedia / FirebaseMediaListener
   * on a successful upload) flips this flag — the legacy ScrollContainer
   * watched it but MediaGallery doesn't use ScrollContainer, so without
   * this effect the new files only appeared on manual reload. */
  useEffect(() => {
    if (!rerender) return;
    refetch();
    dispatch(setRerender(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerender]);

  // ── Filtered/searched view ────────────────────────────────────────────────
  const items = Array.isArray(data) ? data : [];
  const search = filters.search || '';

  const visibleItems = useMemo(() => {
    let arr = items;
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((it) =>
        (it.file_name || it.folder_name || '').toLowerCase().includes(q));
    }
    if (filters.type) {
      // CORNER predates the current type map (it was the accidental
      // classification for png/gif/webp uploads) — treat it as IMAGE here too
      // so the "Image" filter doesn't hide those legacy rows.
      arr = arr.filter((it) => it.is_folder || it.type === filters.type
        || (filters.type === 'IMAGE' && it.type === 'CORNER'));
    }
    return arr;
  }, [items, search, filters.type]);

  // ── Selection helpers ────────────────────────────────────────────────────
  const isSelected = (id) => idsList.includes(String(id));
  const toggleSelect = (id) => {
    const k = String(id);
    setIdsList(idsList.includes(k) ? idsList.filter((x) => x !== k) : [...idsList, k]);
  };
  const selectAll = () => setIdsList(visibleItems.map((i) => String(i.id)));
  const cancelSelect = () => {
    setMultiSelectCheck(false);
    setIdsList([]);
  };

  const onMultiDelete = () => {
    if (idsList.length === 0) return;
    confirmDialog({
      message: t('media.confirm_delete_n', { defaultValue: 'Delete {{n}} items?', n: idsList.length }),
      header: t('buttons.confirm'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteMulti.mutate(idsList, {
          onSuccess: () => {
            showToast('success', 'Media', t('media.deleted_n', { defaultValue: '{{n}} items deleted.', n: idsList.length }));
            cancelSelect();
            dispatch(setRerender(true));
          },
        });
      },
    });
  };

  // ── Per-item action menu (⋮) ──────────────────────────────────────────────
  const menuRef = useRef(null);
  const [menuItem, setMenuItem] = useState(null);
  const openMenu = (event, item) => {
    setMenuItem(item);
    setSelectedItem(item.id);
    menuRef.current?.toggle(event);
  };
  const menuModel = useMemo(() => {
    if (!menuItem) return [];
    if (menuItem.is_folder) {
      return [
        { label: t('buttons.edit'),   icon: 'pi pi-pencil',       command: () => dispatch(toggleDialog('updateFolder')) },
        { label: t('buttons.delete'), icon: 'pi pi-trash',        command: () => dispatch(toggleDeleteDialog('folder')) },
      ];
    }
    return [
      { label: t('quick_show.title'),         icon: 'pi pi-desktop', command: () => setQuickShowItem(menuItem) },
      { separator: true },
      { label: t('media.add_to_playlist',    { defaultValue: 'Add to Playlist' }), icon: 'pi pi-list',         command: () => dispatch(toggleDialog('uploadToPlaylist')) },
      { label: t('media.update',             { defaultValue: 'Rename / Edit' }),   icon: 'pi pi-pencil',       command: () => dispatch(toggleDialog('updateMedia')) },
      { label: t('media.move',               { defaultValue: 'Move to Folder' }),  icon: 'pi pi-folder-open',  command: () => dispatch(toggleDialog('moveMediaFolder')) },
      { separator: true },
      { label: t('buttons.delete'), icon: 'pi pi-trash',
        command: () => {
          fetchMediaUsage.mutate({ id: menuItem.id }, {
            onSuccess: () => dispatch(toggleDialog('deleteMediaDialog')),
          });
        },
      },
    ];
  }, [menuItem, t, dispatch, fetchMediaUsage]);

  // ── Toolbars ──────────────────────────────────────────────────────────────
  const isSelectMode = multiSelectCheck;

  // Mobile icon-only popover menus (refs + models). Sort/Filter become
  // small buttons that open a portal-mounted PrimeReact Menu so the
  // entire toolbar collapses to 2 rows.
  const sortMenuRef     = useRef(null);
  const filterMenuRef   = useRef(null);
  const overflowMenuRef = useRef(null);
  const sortMenuItems = (SORT_OPTIONS || []).map((opt) => ({
    label: opt.label,
    icon: filters.sortOrder === opt.value ? 'pi pi-check' : 'pi pi-circle',
    command: () => setFilters((f) => ({ ...f, sortOrder: opt.value })),
  }));
  const filterMenuItems = [
    {
      label: t('media.all_types', { defaultValue: 'All types' }),
      icon: !filters.type ? 'pi pi-check' : 'pi pi-circle',
      command: () => setFilters((f) => ({ ...f, type: '' })),
    },
    ...((MEDIA_TYPE_OPTIONS || []).map((opt) => ({
      label: opt.label,
      icon: filters.type === opt.value ? 'pi pi-check' : 'pi pi-circle',
      command: () => setFilters((f) => ({ ...f, type: opt.value })),
    }))),
  ];
  const overflowMenuItems = [
    {
      label: t('media.select', { defaultValue: 'Select multiple' }),
      icon: 'pi pi-check-square',
      command: () => setMultiSelectCheck(true),
    },
  ];

  const TopBar = (
    <div className="pp-media-topbar d-none d-md-flex">
      <BreadCrumb
        getData={() => dispatch(galleryRefetch())}
        breadCrumb={breadCrumb}
        setBreadCrumb={setBreadCrumb}
        setCurrentFolder={setCurrentFolder}
      />
      <div className="pp-media-topbar-actions">
        <div className="pp-media-search">
          <i className="pi pi-search" />
          <input
            type="search"
            placeholder={t('media.search_placeholder', { defaultValue: 'Search files…' })}
            value={search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
        </div>
        <button
          className="pp-media-upload-btn"
          onClick={() => dispatch(toggleDialog('uploadMedia'))}
        >
          <i className="pi pi-upload" /> {t('buttons.upload')}
        </button>
      </div>
    </div>
  );

  /* Mobile-only compact 2-row bar.
   * Row 1: search (full width).
   * Row 2: [Upload icon+label] [Folder] [Sort] [Filter] [view-toggle] [⋮].
   * Sort/Filter are icon-only triggers that open a popup Menu; Select
   * is moved into the overflow ⋮ so the main row stays minimal. The
   * desktop TopBar + Toolbar are hidden via the `d-md-*` utility
   * classes added below. */
  const MobileBar = (
    <div className="pp-mg-mobile-bar d-md-none">
      <div className="pp-mg-mb-breadcrumb">
        <BreadCrumb
          getData={() => dispatch(galleryRefetch())}
          breadCrumb={breadCrumb}
          setBreadCrumb={setBreadCrumb}
          setCurrentFolder={setCurrentFolder}
        />
      </div>
      <div className="pp-mg-mb-search">
        <i className="pi pi-search" />
        <input
          type="search"
          placeholder={t('media.search_placeholder', { defaultValue: 'Search files…' })}
          value={search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
        />
      </div>
      <div className="pp-mg-mb-actions">
        <button
          className="pp-mg-mb-upload"
          onClick={() => dispatch(toggleDialog('uploadMedia'))}
        >
          <i className="pi pi-upload" />
          <span className="pp-mg-mb-upload-label">{t('buttons.upload')}</span>
        </button>
        <button
          className="pp-mg-mb-icon"
          onClick={() => dispatch(toggleDialog('createFolder'))}
          aria-label={t('media.new_folder', { defaultValue: 'New Folder' })}
          title={t('media.new_folder', { defaultValue: 'New Folder' })}
        ><i className="pi pi-folder" /></button>
        <button
          className="pp-mg-mb-icon"
          onClick={(e) => sortMenuRef.current?.toggle(e)}
          aria-label={t('media.sort', { defaultValue: 'Sort' })}
          aria-haspopup
          title={t('media.sort', { defaultValue: 'Sort' })}
        ><i className="pi pi-sort-alt" /></button>
        <button
          className={`pp-mg-mb-icon ${filters.type ? 'is-active' : ''}`}
          onClick={(e) => filterMenuRef.current?.toggle(e)}
          aria-label={t('media.filter', { defaultValue: 'Filter' })}
          aria-haspopup
          title={t('media.filter', { defaultValue: 'Filter' })}
        ><i className="pi pi-filter" /></button>
        <div className="pp-view-toggle pp-mg-mb-view">
          <button
            type="button"
            className={layout === 'grid' ? 'is-active' : ''}
            onClick={() => setLayout('grid')}
            aria-pressed={layout === 'grid'}
            title="Grid"
          ><i className="pi pi-th-large" /></button>
          <button
            type="button"
            className={layout === 'list' ? 'is-active' : ''}
            onClick={() => setLayout('list')}
            aria-pressed={layout === 'list'}
            title="List"
          ><i className="pi pi-list" /></button>
        </div>
        <button
          className="pp-mg-mb-icon"
          onClick={(e) => overflowMenuRef.current?.toggle(e)}
          aria-label={t('media.more', { defaultValue: 'More' })}
          aria-haspopup
          title={t('media.more', { defaultValue: 'More' })}
        ><i className="pi pi-ellipsis-v" /></button>
      </div>
      {/* Popups mounted outside the sticky bar AND portalled to body.
       * Two reasons:
       *   1. PrimeReact's absolutePosition() computes top/left in
       *      document-space. When the Menu lives inside the sticky bar
       *      with `appendTo='self'`, its offsetParent is the sticky
       *      element — so the document-space top/left ends up offset by
       *      the sticky's translated position, dropping the popup at
       *      the wrong place.
       *   2. The global `PrimeReact.appendTo = 'self'` (set in index.js
       *      to avoid React 18 removeChild errors) makes 'self' the
       *      default. Overriding per-instance to document.body forces
       *      a body portal so positioning is unambiguous.  */}
      <Menu model={sortMenuItems}     popup ref={sortMenuRef}     appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      <Menu model={filterMenuItems}   popup ref={filterMenuRef}   appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
      <Menu model={overflowMenuItems} popup ref={overflowMenuRef} appendTo={typeof document !== 'undefined' ? document.body : null} className="pp-mg-mb-popup" />
    </div>
  );

  const Toolbar = isSelectMode ? (
    <div className="pp-media-toolbar pp-media-toolbar-select pp-media-toolbar-allow-mobile">
      <span className="pp-media-select-count">
        <strong>{idsList.length}</strong> {t('media.selected', { defaultValue: 'selected' })}
      </span>
      <div className="pp-media-toolbar-spacer" />
      <button className="pp-media-tool-btn" onClick={selectAll}>
        <i className="pi pi-check-square" /> {t('media.select_all', { defaultValue: 'All' })}
      </button>
      <button className="pp-media-tool-btn pp-media-tool-danger" disabled={idsList.length === 0} onClick={onMultiDelete}>
        <i className="pi pi-trash" /> {t('buttons.delete')}
      </button>
      <button className="pp-media-tool-btn pp-media-tool-ghost" onClick={cancelSelect}>
        <i className="pi pi-times" /> {t('buttons.cancel')}
      </button>
    </div>
  ) : (
    <div className="pp-media-toolbar d-none d-md-flex">
      <button className="pp-media-tool-btn" onClick={() => dispatch(toggleDialog('createFolder'))}>
        <i className="pi pi-folder" /> {t('media.new_folder', { defaultValue: 'New Folder' })}
      </button>
      {/* Sort / Filter use the same popup-menu pattern as LinksGallery
       * — the refs (sortMenuRef / filterMenuRef) and item models
       * (sortMenuItems / filterMenuItems) are declared above and shared
       * with the mobile bar. The Menu components themselves are
       * portal-mounted to document.body inside MobileBar, so this
       * button can trigger them from anywhere on the page. */}
      <button className="pp-media-tool-btn" onClick={(e) => sortMenuRef.current?.toggle(e)}>
        <i className="pi pi-sort-alt" /> {t('media.sort', { defaultValue: 'Sort' })}
      </button>
      <button
        className={`pp-media-tool-btn ${filters.type ? 'is-active' : ''}`}
        onClick={(e) => filterMenuRef.current?.toggle(e)}
      >
        <i className="pi pi-filter" /> {filters.type || t('media.filter', { defaultValue: 'Filter' })}
      </button>
      <button className="pp-media-tool-btn" onClick={() => setMultiSelectCheck(true)}>
        <i className="pi pi-list-check" /> {t('media.select', { defaultValue: 'Select' })}
      </button>
      <div className="pp-media-toolbar-spacer" />
      <div className="pp-view-toggle">
        <button
          type="button"
          className={layout === 'grid' ? 'is-active' : ''}
          onClick={() => setLayout('grid')}
          aria-pressed={layout === 'grid'}
          title="Grid view"
        ><i className="pi pi-th-large" /></button>
        <button
          type="button"
          className={layout === 'list' ? 'is-active' : ''}
          onClick={() => setLayout('list')}
          aria-pressed={layout === 'list'}
          title="List view"
        ><i className="pi pi-list" /></button>
      </div>
    </div>
  );

  // ── Item navigation (folder = drill in; file = preview) ───────────────────
  const onItemActivate = (item) => {
    if (item.is_folder) {
      navigate(`/media/gallery/${item.id}`);
    } else {
      setSelectedItem(item.id);
      dispatch(toggleDialog('previewMedia'));
    }
  };

  // ── Apply filter changes once user stops typing / picks options ───────────
  useEffect(() => {
    const tid = setTimeout(() => {
      dispatch(setParams({ ...filters, folder_id: currentFolder?.id || null }));
    }, 300);
    return () => clearTimeout(tid);
  }, [filters, currentFolder?.id]);

  return (
    <Layout padding={false}>
      <FirebaseMediaListener />
      <ConfirmDialog />

      <div className="pp-media-page">
        {MobileBar}
        {TopBar}
        {Toolbar}

        <div className="pp-media-body pp-fab-safe-bottom">
          {isFetching && visibleItems.length === 0 ? (
            <div className="pp-media-loading">
              <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="3" />
            </div>
          ) : visibleItems.length === 0 ? (
            <EmptyState
              icon="pi-folder-open"
              title={t('media.empty_title', { defaultValue: 'No files yet' })}
              description={t('media.empty_desc', { defaultValue: 'Upload your first file to start showing content on your screens.' })}
              cta={{
                icon: 'pi pi-upload',
                label: t('buttons.upload'),
                /* Must match the redux key used by the toolbar's Upload
                 * button (`uploadMedia`). The earlier `uploadFile` key
                 * didn't correspond to any mounted Dialog, so the
                 * empty-state CTA appeared to do nothing. */
                onClick: () => dispatch(toggleDialog('uploadMedia')),
              }}
            />
          ) : layout === 'grid' ? (
            <GridView
              items={visibleItems}
              isSelectMode={isSelectMode}
              isSelected={isSelected}
              toggleSelect={toggleSelect}
              onActivate={onItemActivate}
              onMenu={openMenu}
              onQuickShow={(it) => setQuickShowItem(it)}
              t={t}
            />
          ) : (
            <ListView
              items={visibleItems}
              isSelectMode={isSelectMode}
              isSelected={isSelected}
              toggleSelect={toggleSelect}
              onActivate={onItemActivate}
              onMenu={openMenu}
              onQuickShow={(it) => setQuickShowItem(it)}
              t={t}
            />
          )}
        </div>
      </div>

      {/* Per-item ⋮ menu */}
      <Menu model={menuModel} popup ref={menuRef} className="pp-media-row-menu" />

      {/* Dialog hosts. Both UploadPopup and CreateFolderPopup render their
          OWN trigger Button next to the Dialog — we hide those duplicate
          triggers via .pp-media-dialog-host (CSS rule hides direct-child
          buttons / .flex.justify-end wrappers) so only the topbar/toolbar
          Upload / New Folder controls remain visible. */}
      <div className="pp-media-dialog-host">
        <UploadPopup currentFolder={currentFolder} />
        <CreateFolderPopup
          currentFolder={currentFolder}
          setCurrentFolder={setCurrentFolder}
          setBreadCrumb={setBreadCrumb}
          breadCrumb={breadCrumb}
        />
      </div>
      {openDialog?.updateMedia && selectedItem && (
        <UpdateMedia item={items.find((i) => i.id === selectedItem)} />
      )}
      {openDialog?.moveMediaFolder && selectedItem && (
        <MoveMediaFolder data={items.find((i) => i.id === selectedItem)} />
      )}
      {openDialog?.uploadToPlaylist && selectedItem && (
        <UploadToPlaylist item={items.find((i) => i.id === selectedItem)} />
      )}
      {openDialog?.deleteMediaDialog && selectedItem && fetchMediaUsage?.data && (
        <DeleteMediaDialog
          usage={fetchMediaUsage.data}
          isLoading={deleteMedia.isLoading || fetchMediaUsage.isLoading}
          deleteHandler={() => {
            // useDeleteMedia hits DELETE /media/{payload.id}; selectedItem is the
            // id string, so it must be wrapped as { id } — passing the bare string
            // sent /media/undefined → 404 "media not found".
            deleteMedia.mutate({ id: selectedItem }, {
              onSuccess: () => {
                dispatch(deleteItemLocally(selectedItem));
                dispatch(toggleDialog('deleteMediaDialog'));
              },
            });
          }}
          data={items.find((i) => i.id === selectedItem)}
        />
      )}

      {/* TV-frame preview — mounted here so it works for every media
       * type, including audio (which doesn't render a <LazyImage>
       * thumbnail and therefore couldn't open the LazyImage-mounted
       * preview). */}
      {openDialog?.previewMedia && selectedItem && (() => {
        const previewItem = items.find((i) => i.id === selectedItem);
        return previewItem && !previewItem.is_folder
          ? <PreviewMediaDialog data={previewItem} />
          : null;
      })()}

      {/* Quick Show wizard — opens with media pre-selected */}
      <QuickShowWizard
        open={!!quickShowItem}
        onClose={() => setQuickShowItem(null)}
        media={quickShowItem ? {
          id: quickShowItem.id,
          title: (quickShowItem.file_name || '').replace(/_/g, ' '),
          file_name: quickShowItem.file_name,
          thumb: quickShowItem.thumb,
          type: quickShowItem.type,
          duration: quickShowItem.duration,
        } : null}
      />
    </Layout>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * GRID VIEW
 * ─────────────────────────────────────────────────────────────────── */

function GridView({ items, isSelectMode, isSelected, toggleSelect, onActivate, onMenu, onQuickShow, t }) {
  return (
    <div className="pp-media-grid">
      {items.map((item) => (
        <GridCard
          key={item.id}
          item={item}
          isSelectMode={isSelectMode}
          isSelected={isSelected(item.id)}
          toggleSelect={() => toggleSelect(item.id)}
          onActivate={() => onActivate(item)}
          onMenu={(e) => onMenu(e, item)}
          onQuickShow={() => onQuickShow(item)}
          t={t}
        />
      ))}
    </div>
  );
}

function GridCard({ item, isSelectMode, isSelected, toggleSelect, onActivate, onMenu, onQuickShow, t }) {
  const folder = !!item.is_folder;
  const name   = (item.file_name || item.folder_name || '').replace(/_/g, ' ');
  const meta   = useItemMeta(item);

  return (
    <div className={`pp-media-card ${isSelected ? 'is-selected' : ''} ${folder ? 'is-folder' : ''}`}>
      {/* Selection checkbox (top-left) */}
      {isSelectMode && (
        <div className="pp-media-checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={isSelected} onChange={toggleSelect} />
        </div>
      )}

      {/* Thumbnail */}
      <button
        type="button"
        className="pp-media-thumb"
        onClick={() => (isSelectMode ? toggleSelect() : onActivate())}
      >
        {folder ? (
          <div className="pp-media-thumb-folder-tile">
            <i className="pi pi-folder" />
            <span className="pp-media-folder-count">
              {(() => {
                const n = Number(item.media_count ?? 0);
                if (n === 0) return 'Empty';
                if (n === 1) return '1 file';
                return `${n} files`;
              })()}
            </span>
          </div>
        ) : item.type === 'AUDIO' ? (
          <div className="pp-media-thumb-audio"><i className="pi pi-volume-up" /></div>
        ) : (
          <LazyImage media={item} popupType="media_preview" styleClass="pp-media-thumb-img" mountPreview={false} />
        )}

        {/* Video play overlay + duration badge */}
        {!folder && item.type === 'VIDEO' && (
          <>
            <span className="pp-media-thumb-play"><i className="pi pi-play-circle" /></span>
            {item.duration > 0 && (
              <span className="pp-media-thumb-duration">{formatDuration(item.duration)}</span>
            )}
          </>
        )}

        {/* Hover overlay actions */}
        {!isSelectMode && (
          <div className="pp-media-card-hover">
            {!folder && (
              <button className="pp-media-hover-btn pp-media-hover-primary"
                      onClick={(e) => { e.stopPropagation(); onQuickShow(); }}
                      aria-label={t('quick_show.title')} title={t('quick_show.title')}>
                <i className="pi pi-desktop" />
              </button>
            )}
            <button className="pp-media-hover-btn"
                    onClick={(e) => { e.stopPropagation(); onMenu(e); }}
                    aria-label="More" title="More">
              <i className="pi pi-ellipsis-v" />
            </button>
          </div>
        )}
      </button>

      {/* Info */}
      <div className="pp-media-info">
        <div className="pp-media-name" title={name}>{name}</div>
        <div className="pp-media-meta">{meta}</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * LIST VIEW
 * ─────────────────────────────────────────────────────────────────── */

function ListView({ items, isSelectMode, isSelected, toggleSelect, onActivate, onMenu, onQuickShow, t }) {
  return (
    <div className="pp-media-list-wrap">
      <table className="pp-media-list">
        <thead>
          <tr>
            {isSelectMode && <th className="pp-media-col-check"></th>}
            <th className="pp-media-col-thumb"></th>
            <th className="pp-media-col-name">{t('media.name', { defaultValue: 'Name' })}</th>
            <th className="pp-media-col-type d-none d-sm-table-cell">{t('media.type', { defaultValue: 'Type' })}</th>
            <th className="pp-media-col-duration d-none d-md-table-cell">{t('media.duration', { defaultValue: 'Duration' })}</th>
            <th className="pp-media-col-size d-none d-md-table-cell">{t('media.size', { defaultValue: 'Size' })}</th>
            <th className="pp-media-col-date d-none d-lg-table-cell">{t('media.date', { defaultValue: 'Date' })}</th>
            <th className="pp-media-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ListRow
              key={item.id}
              item={item}
              isSelectMode={isSelectMode}
              isSelected={isSelected(item.id)}
              toggleSelect={() => toggleSelect(item.id)}
              onActivate={() => onActivate(item)}
              onMenu={(e) => onMenu(e, item)}
              onQuickShow={() => onQuickShow(item)}
              t={t}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListRow({ item, isSelectMode, isSelected, toggleSelect, onActivate, onMenu, onQuickShow, t }) {
  const folder = !!item.is_folder;
  const name   = (item.file_name || item.folder_name || '').replace(/_/g, ' ');
  const isTimed = !folder && ['VIDEO', 'AUDIO', 'YOUTUBE', 'DRIVE'].includes(item.type);

  return (
    <tr className={`pp-media-row ${isSelected ? 'is-selected' : ''} ${folder ? 'is-folder' : ''}`}>
      {isSelectMode && (
        <td className="pp-media-col-check" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={isSelected} onChange={toggleSelect} />
        </td>
      )}
      <td className="pp-media-col-thumb">
        <div className="pp-media-list-thumb" onClick={() => (isSelectMode ? toggleSelect() : onActivate())}>
          {folder
            ? <img src={folderIcon} alt="" />
            : item.type === 'AUDIO'
              ? <div className="pp-media-thumb-audio-list"><i className="pi pi-volume-up" /></div>
              : <LazyImage media={item} popupType="media_preview" styleClass="pp-media-list-thumb-img" mountPreview={false} />}
        </div>
      </td>
      <td className="pp-media-col-name" onClick={() => (isSelectMode ? toggleSelect() : onActivate())}>
        <span className="pp-media-row-name" title={name}>{name}</span>
      </td>
      <td className="pp-media-col-type d-none d-sm-table-cell">
        {folder ? t('media.folder', { defaultValue: 'Folder' }) : (item.type || '').toLowerCase()}
      </td>
      <td className="pp-media-col-duration d-none d-md-table-cell">
        {isTimed ? formatDuration(item.duration) : <span className="text-muted">—</span>}
      </td>
      <td className="pp-media-col-size d-none d-md-table-cell">
        {folder
          ? <span className="text-muted">{item.media_count ?? 0} {t('media.files', { defaultValue: 'files' })}</span>
          : (formatSize(item.size) === '0 bytes' ? <span className="text-muted">—</span> : formatSize(item.size))}
      </td>
      <td className="pp-media-col-date d-none d-lg-table-cell">{formatDateTime(item.created_at)}</td>
      <td className="pp-media-col-actions">
        <div className="pp-media-row-actions">
          {!folder && (
            <button className="pp-media-row-action pp-media-row-primary"
                    onClick={() => onQuickShow()}
                    title={t('quick_show.title')} aria-label={t('quick_show.title')}>
              <i className="pi pi-desktop" />
            </button>
          )}
          <button className="pp-media-row-action"
                  onClick={(e) => onMenu(e)}
                  title="More" aria-label="More">
            <i className="pi pi-ellipsis-v" />
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Helpers
 * ─────────────────────────────────────────────────────────────────── */

function useItemMeta(item) {
  if (item.is_folder) {
    // Folder file-count is now shown inside the tile itself; leave the
    // info-row meta blank to avoid showing "3 files" twice.
    return '';
  }
  const parts = [];
  const type = (item.type || '').toLowerCase();
  if (type) parts.push(type.charAt(0).toUpperCase() + type.slice(1));
  if (item.size && formatSize(item.size) !== '0 bytes') parts.push(formatSize(item.size));
  return parts.join(' · ');
}
